import { type LineEnd, type LineFields, LOCAL } from "@mn/shared";
import { memo, useMemo, useSyncExternalStore } from "react";
import type * as Y from "yjs";
import { useBoard } from "./BoardContext";
import type { Rect } from "./camera";
import { useCard, useMapKeys } from "./hooks";
import { useBoardUI } from "./store";

type Side = "top" | "right" | "bottom" | "left";
const anchor = (r: Rect, side: Side) =>
  side === "top"
    ? { x: r.x + r.w / 2, y: r.y }
    : side === "bottom"
      ? { x: r.x + r.w / 2, y: r.y + r.h }
      : side === "left"
        ? { x: r.x, y: r.y + r.h / 2 }
        : { x: r.x + r.w, y: r.y + r.h / 2 };
function pickSides(a: Rect, b: Rect): [Side, Side] {
  const dx = b.x + b.w / 2 - (a.x + a.w / 2);
  const dy = b.y + b.h / 2 - (a.y + a.h / 2);
  return Math.abs(dx) > Math.abs(dy)
    ? [dx > 0 ? "right" : "left", dx > 0 ? "left" : "right"]
    : [dy > 0 ? "bottom" : "top", dy > 0 ? "top" : "bottom"];
}
const out = (s: Side, k: number) =>
  s === "left"
    ? { x: -k, y: 0 }
    : s === "right"
      ? { x: k, y: 0 }
      : s === "top"
        ? { x: 0, y: -k }
        : { x: 0, y: k };

/** SVG layer inside the world: lines follow their cards (live during drags) and are hit-testable on a wide invisible stroke. */
export function Lines() {
  const { handle, index } = useBoard();
  const ids = useMapKeys(handle.types.lines);
  if (ids.length === 0) return null;
  return (
    <svg
      className="absolute left-0 top-0"
      width={1}
      height={1}
      style={{ pointerEvents: "none", zIndex: 2, overflow: "visible" }}
      aria-hidden
    >
      <defs>
        <marker
          id="mn-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="12"
          markerHeight="12"
          markerUnits="userSpaceOnUse"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="context-stroke" />
        </marker>
      </defs>
      {ids.map((id) => {
        const m = handle.types.lines.get(id);
        return m ? <LineView key={id} id={id} ymap={m} index={index} /> : null;
      })}
    </svg>
  );
}

const LineView = memo(function LineView({
  id,
  ymap,
  index,
}: {
  id: string;
  ymap: Y.Map<unknown>;
  index: ReturnType<typeof useBoard>["index"];
}) {
  const l = useCard(ymap) as unknown as LineFields;
  const { handle, canEdit } = useBoard();
  const selected = useBoardUI((s) => s.selectedLine === id);
  const z = useBoardUI((s) => s.camera.z);
  const dragging = useBoardUI((s) => s.dragging);
  const resizing = useBoardUI((s) => s.resizing);
  useSyncExternalStore(index.subscribe, () => index.version);

  const rectFor = (end: LineEnd): Rect | null => {
    if (!end.cardId) return null;
    const r = index.rectOf(end.cardId);
    if (!r) return null;
    const d = dragging?.get(end.cardId);
    const rs = resizing?.id === end.cardId ? resizing : null;
    return { x: d?.x ?? r.x, y: d?.y ?? r.y, w: rs?.w ?? r.w, h: rs?.h ?? r.h };
  };
  const ra = rectFor(l.from);
  const rb = rectFor(l.to);
  const sides = ra && rb ? pickSides(ra, rb) : null;
  const sa = (l.from.side ?? sides?.[0] ?? "right") as Side;
  const sb = (l.to.side ?? sides?.[1] ?? "left") as Side;
  const p = ra ? anchor(ra, sa) : { x: l.from.x, y: l.from.y };
  const q = rb ? anchor(rb, sb) : { x: l.to.x, y: l.to.y };
  const d = useMemo(() => {
    if (l.curve) return `M${p.x},${p.y} Q${l.curve.cx},${l.curve.cy} ${q.x},${q.y}`;
    if (!ra && !rb) return `M${p.x},${p.y} L${q.x},${q.y}`;
    const k = Math.max(40, Math.hypot(q.x - p.x, q.y - p.y) / 2.5);
    const c1 = ra ? out(sa, k) : { x: 0, y: 0 };
    const c2 = rb ? out(sb, k) : { x: 0, y: 0 };
    return `M${p.x},${p.y} C${p.x + c1.x},${p.y + c1.y} ${q.x + c2.x},${q.y + c2.y} ${q.x},${q.y}`;
  }, [p.x, p.y, q.x, q.y, sa, sb, ra, rb, l.curve]);
  const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
  const width = l.weight === "thick" ? 4 : 2;
  return (
    <g>
      <path
        d={d}
        stroke="transparent"
        strokeWidth={14 / z}
        fill="none"
        style={{ pointerEvents: "stroke", cursor: "pointer" }}
        onPointerDown={(e) => {
          e.stopPropagation();
          useBoardUI.getState().select([]);
          useBoardUI.setState({ selectedLine: id });
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (!canEdit) return;
          const label = prompt("Etiqueta / Label", l.label ?? "");
          if (label !== null) handle.doc.transact(() => ymap.set("label", label), LOCAL);
        }}
      />
      <path
        d={d}
        stroke={selected ? "#8aa9ff" : l.color}
        strokeWidth={selected ? width + 1 : width}
        strokeDasharray={l.dashed ? `${width * 3} ${width * 3}` : undefined}
        fill="none"
        strokeLinecap="round"
        markerEnd={l.endHead === "arrow" ? "url(#mn-arrow)" : undefined}
        markerStart={l.startHead === "arrow" ? "url(#mn-arrow)" : undefined}
      />
      {l.label ? (
        <foreignObject x={mid.x - 80} y={mid.y - 14} width={160} height={28} style={{ overflow: "visible" }}>
          <div className="flex justify-center">
            <span className="rounded-sm bg-panel px-2 py-0.5 text-[12px] text-text shadow-panel">
              {l.label}
            </span>
          </div>
        </foreignObject>
      ) : null}
    </g>
  );
});
